import { useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import { METRIC_SPAN_LABEL_MAP } from '@root/entities/pair/ui/constants'
import { capitalize } from '@root/shared/core/StringUtil'
import { DeltaIcon, type Column } from '@root/shared/ui-kit'

import { ExploreTableAddressCell } from './explore-table-address-cell'
import { ExploreTableAuditCell } from './explore-table-audit-cell'
import { ExploreTableBuyCell } from './explore-table-buy-cell'
import { ExploreTableCreatedCell } from './explore-table-created'
import { ExploreTableDevTokensCell } from './explore-table-dev-tokens-cell'
import { ExploreTableDoubleColumnHeader } from './explore-table-double-column'
import { HoldersCell } from './explore-table-holders-cell'
import { LiquidityCell } from './explore-table-liquidity-cell'
import { ExploreTableMcCell } from './explore-table-mc-cell'
import { ExploreTablePairCell } from './explore-table-pair-cell'
import { ExploreTableSwapsCell } from './explore-table-swaps-cell'
import { VolumeCell } from './explore-table-volume-cell'

import type { ExploreTableContext } from './explore-table-types'
import type { PairMetrics, PairModel } from '@root/entities/pair'

const VolumeNetColumn: Column<
  PairModel,
  ExploreTableColumnId,
  ExploreTableContext
> = {
  id: 'volume-net',
  header: () => 'Net',
  sortable: true,
  render: () => undefined,
}
const HoldersChangeColumn: Column<
  PairModel,
  ExploreTableColumnId,
  ExploreTableContext
> = {
  id: 'holders-change',
  header: () => (
    <DeltaPercentRoot>
      <span>%</span>
      <DeltaIcon size={12} />
    </DeltaPercentRoot>
  ),
  sortable: true,
  render: () => undefined,
}

const DeltaPercentRoot = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
`
const CreatedAtColumn: Column<
  PairModel,
  ExploreTableColumnId,
  ExploreTableContext
> = {
  id: 'createdAt',
  header: ({ t }) => t('common.age'),
  render: ({ value }) => <ExploreTableCreatedCell pair={value} />,
  minWidth: 50,
  maxWidth: 80,
  width: '0.7fr',
  sortable: true,
}

export type ExploreTableColumnId =
  | 'pair-address'
  | 'currency'
  | 'currency-address'
  | 'liquidity'
  | 'volume'
  | 'createdAt'
  | 'holders'
  | 'holders-change'
  | 'market-cap'
  | 'volume'
  | 'volume-net'
  | 'swaps'
  | 'audit'
  | 'buy'
  | 'dev-tokens'

export const ExploreColumn = {
  Currency: {
    id: 'currency',
    header: ({ t }) => t('token'),
    render: ({ value }) => <ExploreTablePairCell pair={value} />,
    renderHeader: (props) => {
      return (
        <ExploreTableDoubleColumnHeader
          key={props.column.id}
          {...props}
          auxColumn={CreatedAtColumn}
        />
      )
    },
    minWidth: 250,
    width: '3fr',
  },
  TokenAddress: {
    id: 'currency-address',
    header: ({ t }) => t('currency.token-address'),
    render: ({ value }) => (
      <ExploreTableAddressCell address={value.quote.address} />
    ),
    minWidth: 100,
  },
  PairAddress: {
    id: 'pair-address',
    header: ({ t }) => t('pair.pair-address'),
    render: ({ value }) => <ExploreTableAddressCell address={value.address} />,
    minWidth: 100,
  },
  DevTokens: {
    id: 'dev-tokens',
    header: ({ t }) => capitalize(t('common.dev-tokens')),
    render: ({ value }) => <ExploreTableDevTokensCell pair={value} />,
    minWidth: 150,
  },
  CreatedAt: {
    id: 'createdAt',
    header: ({ t }) => t('common.created'),
    render: ({ value }) => <ExploreTableCreatedCell pair={value} />,
    minWidth: 50,
    maxWidth: 80,
    width: '0.7fr',
    sortable: true,
  },
  Liquidity: {
    id: 'liquidity',
    header: ({ t }) => t('trade.liquidity-short'),
    render: ({ value }) => <LiquidityCell pair={value} />,
    minWidth: 50,
    sortable: true,
  },
  Holders: {
    id: 'holders',
    header: ({ t }) => t('table.holders'),
    render: ({ value, context }) => (
      <HoldersCell pair={value} period={context.period} />
    ),
    renderHeader: (props) => {
      return (
        <ExploreTableDoubleColumnHeader
          key={props.column.id}
          {...props}
          auxColumn={HoldersChangeColumn}
        />
      )
    },
    minWidth: 70,
    sortable: true,
  },
  MarketCap: {
    id: 'market-cap',
    header: ({ t }) => t('trade.market-cap-short'),
    render: ({ value, context }) => (
      <ExploreTableMcCell pair={value} period={context.period} />
    ),
    minWidth: 50,
    sortable: true,
  },
  Volume: {
    id: 'volume',
    header: ({ context, t }) => (
      <PeriodHeader period={context.period} label={t('common.volume_short')} />
    ),
    render: ({ value, context }) => (
      <VolumeCell pair={value} period={context.period} />
    ),
    renderHeader: (props) => {
      return (
        <ExploreTableDoubleColumnHeader
          key={props.column.id}
          {...props}
          auxColumn={VolumeNetColumn}
        />
      )
    },
    minWidth: 90,
    sortable: true,
  },
  Swaps: {
    id: 'swaps',
    header: ({ context, t }) => (
      <PeriodHeader period={context.period} label={t('table.swaps')} />
    ),
    render: ({ value, context }) => (
      <ExploreTableSwapsCell pair={value} period={context.period} />
    ),
    minWidth: 50,
    maxWidth: 90,
    width: '0.7fr',
    sortable: true,
  },
  Audit: {
    id: 'audit',
    header: ({ t }) => t('table.safety_checks'),
    render: ({ value }) => <ExploreTableAuditCell pair={value} />,
    minWidth: 100,
    width: 110,
  },
  Buy: {
    id: 'buy',
    header: ({ t }) => t('table.quick_buy'),
    render: ({ value, context }) => (
      <ExploreTableBuyCell pair={value} feedType={context.feedType} />
    ),
    justify: 'center',
    width: 110,
  },
} satisfies Record<
  string,
  Column<PairModel, ExploreTableColumnId, ExploreTableContext>
>

const PeriodHeader = ({
  period,
  label,
}: {
  period: PairMetrics.MetricsSpan
  label: string
}) => {
  const { t } = useTranslation()
  if (period === 'ALL') {
    return label
  }
  const periodLabel = t(METRIC_SPAN_LABEL_MAP[period])
  return `${periodLabel} ${label}`
}
